import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../api/client';

export default function Login({ onLogin }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.login(form);
      onLogin(res.data.token, res.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      background: '#080b11',
      overflow: 'hidden',
    }}>
      {/* Left — Brand Panel */}
      <div style={{
        flex: '1 1 45%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 48px',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(145deg, #0d1f0a 0%, #080b11 60%)',
      }}>
        {/* Decorative orbs */}
        <div style={{
          position: 'absolute', top: '10%', left: '15%',
          width: 280, height: 280, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(163,230,53,0.15) 0%, transparent 70%)',
          animation: 'float 6s ease-in-out infinite',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '15%', right: '10%',
          width: 200, height: 200, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(74,222,128,0.1) 0%, transparent 70%)',
          animation: 'float 8s ease-in-out infinite reverse',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', top: '55%', left: '5%',
          width: 140, height: 140, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(129,140,248,0.1) 0%, transparent 70%)',
          animation: 'float 7s ease-in-out infinite 2s',
          pointerEvents: 'none',
        }} />

        {/* Brand content */}
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 340 }}>
          <div style={{
            width: 68, height: 68, borderRadius: 18,
            background: 'linear-gradient(135deg, #a3e635 0%, #4ade80 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 32, margin: '0 auto 28px',
            boxShadow: '0 0 40px rgba(163,230,53,0.3)',
          }}>🥗</div>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: 16, color: '#f1f5f9', letterSpacing: '-0.03em' }}>
            NutriTrack
          </h1>
          <p style={{ color: 'rgba(241,245,249,0.5)', fontSize: 15, lineHeight: 1.7 }}>
            Track your nutrition, understand your body, and reach your health goals — all in one place.
          </p>

          {/* Features list */}
          <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', gap: 16, textAlign: 'left' }}>
            {[
              { icon: '🤖', text: 'AI-powered meal analysis' },
              { icon: '📅', text: 'Visual nutrition calendar' },
              { icon: '📊', text: 'Daily macro tracking' },
            ].map(({ icon, text }) => (
              <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: 'rgba(163,230,53,0.1)',
                  border: '1px solid rgba(163,230,53,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, flexShrink: 0,
                }}>{icon}</div>
                <span style={{ color: 'rgba(241,245,249,0.7)', fontSize: 14 }}>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — Auth Form */}
      <div style={{
        flex: '1 1 55%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 40px',
      }}>
        <form
          onSubmit={handleSubmit}
          style={{
            width: '100%',
            maxWidth: 420,
            animation: 'fadeInUp 0.5s cubic-bezier(0.4,0,0.2,1) both',
          }}
        >
          <div style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: '1.7rem', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 8 }}>
              Welcome back
            </h2>
            <p style={{ color: 'rgba(241,245,249,0.45)', fontSize: 14 }}>
              Sign in to your NutriTrack account
            </p>
          </div>

          {error && (
            <div className="error-banner" style={{ marginBottom: 24 }}>
              ⚠️ {error}
            </div>
          )}

          <div className="input-group">
            <label>Email address</label>
            <input
              type="email"
              name="email"
              required
              value={form.email}
              onChange={handleChange}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </div>

          <div className="input-group" style={{ marginBottom: 28 }}>
            <label>Password</label>
            <input
              type="password"
              name="password"
              required
              value={form.password}
              onChange={handleChange}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', height: 48, fontSize: 15 }}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner" />
                Signing in…
              </>
            ) : (
              'Sign in →'
            )}
          </button>

          <p style={{ marginTop: 24, fontSize: 14, textAlign: 'center', color: 'rgba(241,245,249,0.4)' }}>
            Don't have an account?{' '}
            <Link to="/signup" style={{ color: '#a3e635', fontWeight: 600 }}>
              Create one
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
